import debounce from 'lodash-es/debounce.js'

type Constructor = new (...args: any[]) => {}

/**
 * A higher-order function that enhances a base class with infinite scrolling functionality.
 *
 * @template TBase - The base class constructor type.
 * @param {TBase} Base - The base class to enhance with infinite scrolling.
 * @returns {TBase & InfiniteScrollable} - The extended class with infinite scrolling capabilities.
 */
export default function InfiniteScrollable<TBase extends Constructor>(Base: TBase) {
  return class Scrolling extends Base {
    /**
     * Default options for infinite scrolling.
     *
     * @type {object}
     * @property {number} threshold - The threshold value to trigger loading more items.
     * @property {number} limit - The limit of items to load per page.
     * @property {string} totalProp - The property name for the total number of items.
     * @property {string} pageProp - The property name for the current page number.
     */
    _infiniteScrollOptions = {
      threshold: 20,
      limit: 30,
      totalProp: '_total',
      pageProp: '_page'
    }

    /**
     * Event handler for the scroll event with debouncing.
     *
     * @param {Event} e - The scroll event object.
     */
    onScroll = debounce(e => {
      //@ts-ignore inheritted class should have scrollTargetEl property
      var el = this.scrollTargetEl
      if (!el) {
        console.warn('scroll target element is not defined.')
        return
      }

      var { threshold = 0, limit, totalProp, pageProp } = this._infiniteScrollOptions

      if (
        (this as any)[pageProp] < (this as any)[totalProp] / limit &&
        el.scrollHeight - el.clientHeight <= el.scrollTop + threshold
      ) {
        this.scrollAction()
      }
    }, 300)

    // commented due to TS2611 error
    // get scrollTargetEl(): HTMLElement | null {
    //   console.warn('scroll target element is not defined.')

    //   return null
    // }

    /**
     * An async function to handle scroll action when the threshold is reached.
     */
    async scrollAction() {
      console.warn('scroll action not implemented.')
    }
  }
}
